<template>
  <div class="tabs">
    <div class="container">
      <div class="tabs__header relative row pt20 pb20">
        <div class="col-md-4" v-for="(name, index) in tabsNames" :key="index">
          <p @click="changeActiveTab(index)" class="h3 weight-500 my10" :class="{ 'active' : activeTab === index}">
            {{ $t(name) }}
          </p>
          <router-link :to="linkByName(name)" class="hidden-xs cl-main h5 mt0 mb10">
            {{ $t('see all') }} >
          </router-link>
          <router-link v-show="activeTab === index" :to="linkByName(name)" class="visible-xs cl-main h5 m0 see-all-xs">
            {{ $t('see all') }} >
          </router-link>
        </div>
      </div>
      <div class="tabs__body row">
        <div class="tabs__column col-md-4 flex" v-for="(name, index) in tabsNames" :key="index + '-product'" :class="activeTab === index || !$device.isMobile ? '' : 'visually-hidden-carousel'">
          <div v-show="!$device.isMobile && lastPage(name) > 1" class="flex middle-xs center-xs pagination-tabs">
            <div class="arrow-container" @click="loadPreviusPage(name)">
              <i class="arrow top arrow-position-top" />
            </div>
            <span
              v-for="(page, paginationIndex) in lastPage(name)"
              :key="paginationIndex + page + '-pagination'"
              @click="loadPageWithIndex(name, paginationIndex + 1)"
              :class="{ 'active' : paginationIndex + 1 === currentPages[name] }"
              class="pagination-dot"
            />
            <div class="arrow-container" @click="loadNextPage(name)">
              <i class="arrow down arrow-position-down" />
            </div>
          </div>
          <div class="w-100">
            <product-tabs-tile
              v-show="!$device.isMobile"
              class="tabs__product fade-in"
              :list="name" :position="index"
              :product="product"
              v-for="(product) in productPage(name)"
              :key="product.id"
              :omnibus-price="omnibusPriceMap[product.sku]"
            />
          </div>
          <products-carousel :class="$device.isMobile ? '' : 'visually-hidden-carousel'" :is-main-page="true" :list="name" :products="products[name]" @page-changed="loadAll" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProductTabsTile from './ProductTabsTile'
import ProductsCarousel from 'theme/components/core/ProductsCarousel.vue'
import { prepareQuery } from '@vue-storefront/core/modules/catalog/queries/common'
import { mapGetters } from 'vuex'
import {
  fetchOmnibusPriceList,
  getProductsOnSaleSkus,
  proccessPrices
} from 'theme/helpers'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'

export default {
  components: {
    ProductTabsTile,
    ProductsCarousel
  },
  props: {
    products: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      omnibusPriceMap: {},
      currentPages: {
        'Bestseller': 1,
        'New': 1,
        'Sale': 1
      },
      activeTab: 0,
      selectedElement: new Set()
    }
  },
  mounted () {
    this.selectedElement.clear()
  },
  computed: {
    ...mapGetters({
      BestsellerTotal: 'homepage/getBestsellersTotal',
      NewTotal: 'homepage/getNewsTotal',
      SaleTotal: 'homepage/getSalesTotal'
    }),
    currencyCode () {
      return currentStoreView().i18n.currencyCode
    },
    tabsNames () {
      return this.products
        ? Object.keys(this.products)
        : null
    }
  },
  watch: {
    products: {
      handler (newVal) {
        let tabsProducts = Object.values(newVal)
        if (tabsProducts && tabsProducts.length) {
          tabsProducts = tabsProducts && [].concat(...tabsProducts)
          this.updateOmnibusPriceMap(tabsProducts)
        }
      },
      immediate: true
    }
  },
  methods: {
    async updateOmnibusPriceMap (tabsProducts) {
      if (!(tabsProducts && tabsProducts.length)) return
      const productsInSaleSkus = getProductsOnSaleSkus(tabsProducts)
      if (!productsInSaleSkus || !productsInSaleSkus.length) return

      let omnibusPriceMap = await fetchOmnibusPriceList(productsInSaleSkus, this.currencyCode)
      if (!omnibusPriceMap) return
      omnibusPriceMap = proccessPrices(omnibusPriceMap)
      this.omnibusPriceMap = Object.assign({}, omnibusPriceMap, this.omnibusPriceMap)
    },
    productPage (type) {
      let firstNumber = (this.currentPages[type] * 3) - 3
      return this.products[type].slice(firstNumber, firstNumber + 3)
    },
    loadPreviusPage (type) {
      if (this.currentPages[type] > 1) {
        this.currentPages[type] -= 1
      }
    },
    lastPage (type) {
      if (type === 'Bestseller') {
        return Math.ceil(this.BestsellerTotal / 3)
      } else if (type === 'New') {
        return Math.ceil(this.NewTotal / 3)
      } else if (type === 'Sale') {
        return Math.ceil(this.SaleTotal / 3)
      } else {
        return 0
      }
    },
    getQueryConfig (type) {
      let queryConfig, sort, commitName, total
      if (type === 'Bestseller') {
        queryConfig = 'bestseller'
        sort = 'sales_count:desc'
        commitName = 'LOAD_MORE_BESTSELLERS'
        total = this.BestsellerTotal
      } else if (type === 'New') {
        queryConfig = 'news_to_date'
        sort = 'id:desc'
        commitName = 'LOAD_MORE_NEWS'
        total = this.NewTotal
      } else if (type === 'Sale') {
        queryConfig = 'special_price'
        sort = 'view_count:desc'
        commitName = 'LOAD_MORE_SALES'
        total = this.SaleTotal
      }
      return { queryConfig, sort, commitName, total }
    },
    async loadNextPage (type) {
      let { queryConfig, sort, commitName } = this.getQueryConfig(type)
      let startPoint = this.products[type] ? this.products[type].length : 0
      let loadedPages = ((this.currentPages[type] + 1) * 3)
      // First fix for animation && dont make dispatch if it is last page
      if (type === 'Bestseller' && Math.ceil(this.BestsellerTotal / 3) === this.currentPages[type]) {
        return
      } else if (type === 'Sale' && Math.ceil(this.SaleTotal / 3) === this.currentPages[type]) {
        return
      } else if (type === 'New' && Math.ceil(this.NewTotal / 3) === this.currentPages[type]) {
        return
      }
      if (startPoint < loadedPages) {
        const newProductsQuery = prepareQuery({ queryConfig })
        const { items } = await this.$store.dispatch('product/findProducts', {
          query: newProductsQuery,
          size: 3,
          start: startPoint,
          sort: sort,
          options: {
            populateRequestCacheTags: true,
            prefetchGroupProducts: false
          }
        }, { root: true })
        this.$store.commit('homepage/' + commitName, items)
      }
      if (this.currentPages[type] < this.lastPage(type)) {
        this.currentPages[type] += 1
      }
    },
    async loadPageWithIndex (type, number) {
      let { queryConfig, sort, commitName } = this.getQueryConfig(type)
      const newProductsQuery = prepareQuery({ queryConfig })
      let startPoint = this.products[type] ? this.products[type].length : 0
      let sizeToLoad = (number * 3) - startPoint
      if (sizeToLoad > 0) {
        const { items } = await this.$store.dispatch('product/findProducts', {
          query: newProductsQuery,
          size: sizeToLoad,
          start: startPoint,
          sort: sort,
          options: {
            populateRequestCacheTags: true,
            prefetchGroupProducts: false
          }
        }, { root: true })
        this.$store.commit('homepage/' + commitName, items)
      }
      this.currentPages[type] = number
    },
    async loadAll (dataFromChild) {
      let { currentPage, perPage, type } = dataFromChild
      if (this.selectedElement.has(type)) return
      let { queryConfig, sort, commitName, total } = this.getQueryConfig(type)
      const newProductsQuery = prepareQuery({ queryConfig })
      let startPoint = 3
      if (total > 20) total = 19
      let sizeToLoad = total - 3
      if (sizeToLoad > 0 || total) {
        const { items } = await this.$store.dispatch('product/findProducts', {
          query: newProductsQuery,
          size: sizeToLoad,
          start: startPoint,
          sort: sort,
          options: {
            populateRequestCacheTags: true,
            prefetchGroupProducts: false
          }
        }, { root: true })
        this.$store.commit('homepage/' + commitName, items)
      }
      this.selectedElement.add(type)
    },
    linkByName (name) {
      if (name === 'Bestseller') {
        return '/sklep?bestsellery=true'
      } else if (name === 'New') {
        return '/sklep?new=true&sort=id%3Adesc'
      } else if (name === 'Sale') {
        return '/sklep?sale=true&sort=view_count%3Adesc'
      } else {
        return '/sklep'
      }
    },
    changeActiveTab (index) {
      this.activeTab = index
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';

$forest-green: color(forest-green);

  .fade-in {
    opacity: 1;
    animation-name: fadeInOpacity;
    -webkit-animation-name: fadeInOpacity;
    animation-iteration-count: 1;
    -webkit-animation-iteration-count: 1;
    animation-timing-function: ease-in;
    -webkit-animation-timing-function: ease-in;
    animation-duration: 0.7s;
    -webkit-animation-duration: .7s;
  }

  @keyframes fadeInOpacity {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
  }

.visually-hidden-carousel {
  position: absolute;
  overflow: hidden;
  clip: rect(0 0 0 0);
  width: 100%;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
}
.see-all-xs {
  position: absolute;
  left: 20px;
  bottom: 10px;
}
.arrow-container {
  width: 40px;
  height: 40px;
  // border: 2px solid #1396EC;
  border-radius: 50%;
  position:  relative;
  display: flex;
  align-items: center;
  justify-content: center;

  .arrow {
    position: absolute;
    border: solid #1396EC;
    border-width: 0 2px 2px 0;
    display: inline-block;
    padding: 5px;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
  .arrow-position-top {
    top: calc(50% - 2px)
  }
  .arrow-position-down {
    top: calc(50% - 7px)
  }
  .top {
    transform: rotate(-135deg);
    -webkit-transform: rotate(-135deg);
  }
  .down {
    transform: rotate(45deg);
    -webkit-transform: rotate(45deg);
  }
}
.tabs {
  background-color: white;
  @media (max-width: 767px) {
    background: unset;
  }
  &__header {
    div {
      @media (max-width: 767px) {
        -ms-flex: 1 1 0;
        flex: 1 1 0;
        min-width: 120px;
        text-align: center;
      }
    }
    p {
      @media (max-width: 767px) {
        &.active {
          color: $forest-green;
          position: relative;
          &:after {
            opacity: 1;
            bottom: calc(50% - 25px);
          }
        }
        &:after {
          content: '';
          position: absolute;
          display: inline-block;
          width: 10px;
          height: 10px;
          opacity: 0;
          bottom: 50%;
          left: calc(50% - 5px);
          -moz-border-radius: 5px;
          -webkit-border-radius: 5px;
          border-radius: 5px;
          background-color: $forest-green;
          transition: 0.3s;
        }
        font-size: 16px;
        margin: 0;
        padding-top: 10px;
        padding-bottom: 10px;
        color: #CFCFCF;
      }
    }
    @media (max-width: 767px) {
      -webkit-overflow-scrolling: touch;
      overflow-x: auto;
      flex-wrap: nowrap;
      -ms-flex-wrap: nowrap;
      padding-bottom: 35px;
    }
  }
  &__column {
    @media (max-width: 767px) {
      overflow-x: auto;
      flex-wrap: nowrap;
      -ms-flex-wrap: nowrap;
      display: flex;
      -webkit-overflow-scrolling: touch;
      padding-bottom: 30px;
    }
  }
  ::-webkit-scrollbar-thumb {
    background: unset;
  }
  ::-webkit-scrollbar-track {
    background: unset;
  }
}
.pagination-tabs {
  width: 50px;
  display: flex;
  padding-right: 15px;
  flex-direction: column;
  -ms-flex-direction: column;
  .pagination-dot {
    // &:hover {
    //   width: 15px;
    //   height: 15px;
    // }
    &.active {
      background-color: #1396EC;
      width: 15px;
      height: 15px;
    }
    width: 12px;
    height: 12px;
    background-color: rgb(207, 207, 207);
    margin-top: 7px;
    margin-bottom: 7px;
    border-radius: 50%;
    transition: 0.3s all ease;
  }
}
</style>
